/**
 * JobSafetyAudit Domain Model
 * ===========================
 *
 * Purpose:
 * Represents a flattened, normalized record of a Job Safety Audit (JSA) form submission.
 * The reactive form component builds a nested structure which is transformed into this
 * model prior to persistence / transmission. All checkbox boolean values are converted to
 * semantic string tokens ('Yes' | 'No' | 'NA') upstream, so this class stores only string
 * values for those fields to simplify downstream rendering and reporting.
 *
 * Distinguishing Characteristics:
 * - Exposure Normalization: When `fallExposure` is 'No', all checkbox-derived fields are
 *   expected (contract) to be the literal 'NA'. The component enforces this before instantiation.
 * - View vs Submission Steps: Two static step metadata providers are exposed:
 *   - `getFormSubmissionSteps()` groups related fields with checkbox collections for interactive entry.
 *   - `getFormSteps()` flattens each conceptual section into its own step for read-only viewing.
 * - Field Mappings centralize human-readable labels decoupled from internal property names.
 *
 * Data Integrity Contract:
 * - Dates: `date` is parsed as a local date (no time) using `parseLocalDate`; `submissionDate` is a full Date.
 * - Non-nullable conceptual properties use definite assignment assertion (`!`) because they are
 *   populated via object spread during construction from a sanitized source object.
 * - No runtime validation logic resides here; enforcement happens in the component layer.
 *
 * Extension Guidance:
 * - To add new checkbox groups: update mappings + step metadata; the component's dynamic aggregation
 *   will incorporate them automatically if exposed in `getFormSubmissionSteps`.
 * - Preserve consistent naming: `other<X>Specify` pairs with `<x>Other` checkbox to enable conditional validation.
 *
 * @author MacSafety Development
 * @version 1.1
 */
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormAttachmentUploadRequirements } from '../attachments/form-attachment-requirements';
import { GeneralFormData } from '../general-form-data.model';
import { FormStep } from '../form-steps.interface';
import { parseLocalDate } from '../../../utils/date-time-formatter';

export class JobSafetyAudit {
  id?: string;
  submittedByName: string = '';
  submittedByEmail: string = '';
  submissionDate?: Date;
  date: Date | null = null;
  callNumber: string = '';
  location: string = '';

  /**
   * Section 1: FALL HAZARDS
   * All properties (except `fallExposure`) represent individual hazard indicators.
   * Expected Values: 'Yes' | 'No' | 'NA' (see exposure normalization logic in component).
   */
  fallExposure: string = ''; // ARE YOU EXPOSED TO FALLS GREATER THAN 4 FT
  openSidedSpaces!: string; // OPEN SIDED WALKING/WORKING SPACES
  skylightOpenings!: string; // SKYLIGHT OPENINGS
  floorOpenings!: string; // FLOOR OPENINGS
  wallOpenings!: string; // WALL OPENINGS
  openSidedRamps!: string; // OPENSIDED RAMPS, RUNWAYS OR PLATFORMS
  boomLifts!: string; // BOOM LIFTS
  trenches!: string; // TRENCHES
  fallHazardsOther!: string; // OTHER SPECIFY
   otherFallHazardsSpecify!: string;

  /**
   * Section 2: METHODS OF FALL PROTECTION
   * Expected Values: 'Yes' | 'No' | 'NA'.
   */
  guardrailSystem!: string; // GUARDRAIL SYSTEM, PARAPET WALL ARE GREATER THAN 39"
  warningLine!: string; // WARNING LINE - HI VIS TAPE 36" FROM SURFACE >15' FROM HAZARD
  fallRestraintSystem!: string; // PERSONAL FALL RESTRAINT SYSTEM (HARNESS, DROPLINE, ROPE GRAB)
  fallArrestSystem!: string; // PERSONAL FALL ARREST SYSTEM (HARNESS, LANYARD, DROPLINE, SRL)
  fallProtectionOther!: string; // OTHER SPECIFY
  otherFallProtectionSpecify!: string;

  /**
   * Section 3: PROCEDURES FOR ASSEMBLY / MAINT / INSPECT / DISASSEMBLY
   * Expected Values: 'Yes' | 'No' | 'NA'.
   */
  walkToEquipment!: string; // WILL WALK DIRECTLY TO EQUIPMENT/EGRESS POINT
  connectToEquipment!: string; // CONNECT DIRECTLY TO EQUIPMENT/GEAR INSPECTED
  suitableAnchor!: string; // SUITABLE ANCHOR (4X RESTRAINT/5000LBS ARREST/GEAR INSPECTED
  droplineWithRopegrab!: string; // DROPLINE W/ ROPEGRAB LANYARD FULL BDY HARNESS/GEAR INSPECTED
  existingFallProtectAnchor!: string; // EXISTING FALL PROTECT ANCHOR FIXED TO ROOF/GEAR INSPECTED
  proceduresOther!: string; // OTHER SPECIFY
  otherProceduresSpecify!: string;

  /**
   * Section 4: METHODS OF OVERHEAD PROTECTION
   * Expected Values: 'Yes' | 'No' | 'NA'.
   */
  noFallingHazards!: string; // NO FALLING HAZARDS EXIST
  hardHats!: string; // HARD HATS
  barricades!: string; // BARRICADES
  warningSigns!: string; // POSTED WARNING SIGNS
  materialSecured!: string; // MATERIAL SECURED WHEN NOT IN USE
  overheadProtectionOther!: string; // OTHER SPECIFY
  otherOverheadProtectionSpecify!: string;

  /**
   * Section 5: RESCUE PLAN
   * Expected Values: 'Yes' | 'No' | 'NA'.
   */
  call911!: string; // CALL 911
  staffMonitoring!: string; // BUILDING/CUSTOMER STAFF IS MONITORING MY SAFETY
  suspensionTrauma!: string; // SUSPENSION/TRAUMA ATTACHED TO HARNESS
  utilizeAerialLift!: string; // UTILIZE AERIAL/SCISSOR LIFT
  utilizePreStageLadders!: string; // UTLIZE PRE-STAGE LADDERS
  rescuePlanOther!: string; // OTHER SPECIFY
  otherRescuePlanSpecify!: string;

  /**
   * Section 6: WORKING UNDER PLAN
   * Free-form textual fields; not part of exposure checkbox normalization.
   */
  techniciansWorkingUnderPlan: string = '';
  additionalComments: string = '';
  technicianName: string = '';

  // othersWorkingUnderPlan: string='';
  // riskControlMeasures: string = '';
  // personalProtectiveEquipment: string = '';
  // technicianSignature: Array<string> = [];

  /**
   * Constructs a new `JobSafetyAudit` instance optionally hydrating with persisted / form data.
   *
   * Parsing Rules:
   * - `date`: Interpreted as a local date (no time preservation) using `parseLocalDate` to avoid TZ drift.
   * - `submissionDate`: Treated as an absolute timestamp; retained as-is when provided.
   *
   * Safety:
   * - Uses `Object.assign` to hydrate only known fields; additional properties on the
   *   incoming partial object are ignored by TypeScript structural typing.
   *
   * Idempotency:
   * - Reconstructing a model from its own serialized plain object (when date fields are preserved)
   *   yields equivalent semantic values.
   */
  constructor(data?: Partial<JobSafetyAudit>) {
    if (data) {
      Object.assign(this, {
        ...data,
        date: data.date ? parseLocalDate(data.date as any) : null,
        submissionDate: data.submissionDate ? new Date(data.submissionDate) : null,
      });
    }
  }

  // getTimeString(): string {
  //   const now = new Date();
  //   const pstTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
  //   return pstTime.toTimeString().slice(0, 5); // Format as HH:MM for time input
  // }

  /**
   * Provides static metadata consumed by higher-level form registries for routing,
   * display cards, and attachment enforcement.
   * @returns GeneralFormData describing identity & presentation of the form.
   */
  static getFormInfo(): GeneralFormData {
    return {
      formType: 'job-safety-audit',
      formTitle: 'Job Safety Audit',
      formIcon: 'clipboard-outline',
      cardImage: 'assets/form-card-tiles/job-safety-audit.png',
      formAttachmentRequirements: {
        // signatures: [
        //   {
        //     identifier: 'technicianSignature',
        //     label: this.getFieldMappings()['technicianSignature'],
        //     required: true,
        //   }
        // ],
      },
    };
  }

  // Object that shows steps and sections with fields for each section in the form submission process
  /**
   * Submission Step Metadata
   * -------------------------
   * Defines a single logical step containing multiple conceptual sections.
   * Checkbox groups are declared here so the component can dynamically build reactive
   * form controls without hard-coding field lists in multiple places.
   *
   * Note: The UI is configured to suppress multi-step navigation since only one step exists.
   */
  static getFormSubmissionSteps(): FormStep[] {
    return [
      {
        id: 'basic',
        shortTitle: 'Basic Info',
        title: 'Basic Information',
        icon: 'person-outline',
        description: 'Enter the basic information',
        sections: [
          {
            title: 'Basic Information',
            fields: ['date', 'callNumber', 'location'],
          },

          //single-step section for Fall Hazards
          {
            title: '1. Fall Hazards',
            checkboxGroups: [
              {
               checkboxLabel: this.getFieldMappings()['fallHazards'],
                fields: [

                  'openSidedSpaces',
                  'skylightOpenings',
                  'floorOpenings',
                  'wallOpenings',
                  'openSidedRamps',
                  'boomLifts',
                  'trenches',
                  'fallHazardsOther',
                ],
              },
            ],
            fields: ['fallExposure','otherFallHazardsSpecify'],
          },


//single-step section for Methods of Fall Protection

            {
            title: '2. Methods of Fall Protection',
            checkboxGroups: [
              {
               checkboxLabel: this.getFieldMappings()['fallProtection'],
                fields: [
              'guardrailSystem',
              'warningLine',
              'fallRestraintSystem',
              'fallArrestSystem',
              'fallProtectionOther',
            
                ],
              },
            ],
            fields: ['otherFallProtectionSpecify'],
          },

//single-step section for Procedures for Assembly, Maintenance, Inspection, Disassembly

                 {
            title: '3. Procedures for Assembly, Maintenance, Inspection, Disassembly',
            checkboxGroups: [
              {
               checkboxLabel: this.getFieldMappings()['procedures'],
                fields: [
                  'walkToEquipment',
                  'connectToEquipment',
                  'suitableAnchor',
                  'droplineWithRopegrab',
                  'existingFallProtectAnchor',
                  'proceduresOther',
                ],
              },
            ],
            fields: ['otherProceduresSpecify'],
          },

//single-step section for Methods of Overhead Protection

                 {
            title: '4. Methods of Overhead Protection',
            checkboxGroups: [
              {
               checkboxLabel: this.getFieldMappings()['overheadProtection'],
                fields: [
                  'noFallingHazards',
                  'hardHats',
                  'barricades',
                  'warningSigns',
                  'materialSecured',
                  'overheadProtectionOther',
                ],
              },
            ],
            fields: ['otherOverheadProtectionSpecify'],
          },

//single-step section for Rescue Plan
                 {
            title: '5. Rescue Plan',
            checkboxGroups: [
              {
               checkboxLabel: this.getFieldMappings()['rescuePlan'],
                fields: [
                  'call911',
                  'staffMonitoring',
                  'suspensionTrauma',
                  'utilizeAerialLift',
                  'utilizePreStageLadders',
                  'rescuePlanOther',
                ],
              },
            ],
            fields: ['otherRescuePlanSpecify'],
          },

//single-step section for Working Under Plan
                
                 {
            title: '6. Working Under Plan',
            fields: ['techniciansWorkingUnderPlan', 'additionalComments'],
          }
        ]
      }
    ];
  }



// Object that shows steps and sections with fields for each section in the form view 
  /**
   * View Step Metadata
   * -------------------
   * Provides an alternate representation for read-only / detail views where each
   * conceptual section becomes its own independent step. Checkbox groups are
   * flattened into plain field arrays to simplify templating in view contexts.
   */
  static getFormSteps(): FormStep[] {
    // For view mode: each conceptual section becomes its own step with a single (untitled) section.
    // Checkbox groups are flattened into plain fields here.
    return [
      {
        id: 'basic',
        shortTitle: 'Basic Info',
        title: 'Basic Information',
        icon: 'person-outline',
        sections: [
          { title: '', fields: ['date', 'callNumber', 'location'] }
        ]
      },
      {
        id: 'fall-hazards',
        shortTitle: 'Fall Hazards',
        title: 'Fall Hazards',
        icon: 'warning-outline',
        sections: [
          { title: '', fields: [
            'fallExposure',
            'openSidedSpaces','skylightOpenings','floorOpenings','wallOpenings','openSidedRamps','boomLifts','trenches','fallHazardsOther','otherFallHazardsSpecify'
          ]}
        ]
      },
      {
        id: 'fall-protection',
        shortTitle: 'Fall Protection',
        title: 'Methods of Fall Protection',
     icon: 'shield-checkmark-outline',
        sections: [
          { title: '', fields: [
            'guardrailSystem','warningLine','fallRestraintSystem','fallArrestSystem','fallProtectionOther','otherFallProtectionSpecify'
          ]}
        ]
      },
      {
        id: 'procedures',
        shortTitle: 'Procedures',
        title: 'Procedures for Assembly, Maintenance, Inspection, Disassembly',
        icon: 'construct-outline',
        sections: [
          { title: '', fields: [
            'walkToEquipment','connectToEquipment','suitableAnchor','droplineWithRopegrab','existingFallProtectAnchor','proceduresOther','otherProceduresSpecify'
          ]}
        ]
      },
      {
        id: 'overhead-protection',
        shortTitle: 'Overhead Prot.',
        title: 'Methods of Overhead Protection',
     icon: 'shield-checkmark-outline',
        sections: [
          { title: '', fields: [
           'noFallingHazards','hardHats','barricades','warningSigns','materialSecured','overheadProtectionOther','otherOverheadProtectionSpecify'
          ]}
        ]
      },
      {
        id: 'rescue-plan',
        shortTitle: 'Rescue Plan',
        title: 'Rescue Plan',
           icon: 'medkit-outline',
        sections: [
          { title: '', fields: [
            'call911','staffMonitoring','suspensionTrauma','utilizeAerialLift','utilizePreStageLadders','rescuePlanOther','otherRescuePlanSpecify'
          ]}
        ]
      },
      {
        id: 'working-under-plan',
        shortTitle: 'Working Under Plan',
        title: 'Working Under Plan',
        icon: 'people-outline',
        sections: [
          { subtitle: 'I declare that I fully understand the safety requirements listed for this job and undertake to comply in full with these requirements at all times while performing my duties and ensure adequate safety supervision of apprentices under my charge',
            fields: ['techniciansWorkingUnderPlan','additionalComments', 'technicianName'] }
        ]
      }
    ];
  }





  

  /**
   * Field Label Mappings
   * --------------------
   * Central source of human-readable labels keyed by internal property names.
   * Used by: components, PDF generation, exports, and dynamic checkbox label resolution.
   *
   * Maintenance Guidelines:
   * - Keep keys synchronized with property names; refactors should include search/replace here.
   * - Avoid embedding formatting or punctuation logic; keep values presentation-ready.
   *
   * @returns map of field name -> user-facing label.
   */
  static getFieldMappings(): Record<string, string> {
    return {
      submittedByName: 'Submitter Name',
      submittedByEmail: 'Submitter Email',
      submissionDate: 'Submission Date',
      date: 'Date',
      time: 'Time',
      customer: 'Customer',
      callNumber: 'Call #',
      location: 'Location',
      description: 'Description',

      // Section 1: FALL HAZARDS
      fallHazards: 'Select all Fall Hazards that Apply',
      fallExposure: 'Are you exposed to falls greater than 4 ft',
      openSidedSpaces: 'Open sided walking/working spaces',
      skylightOpenings: 'Skylight openings',
      floorOpenings: 'Floor openings',
      wallOpenings: 'Wall openings',
      openSidedRamps: 'Opensided ramps, runways or platforms',
      boomLifts: 'Boom lifts',
      trenches: 'Trenches',
      fallHazardsOther: 'Other Fall Hazards',
      otherFallHazardsSpecify: 'Specify Other Fall Hazards',

      // Section 2: METHODS OF FALL PROTECTION
      fallProtection: 'Select all Methods of Fall Protection that Apply',
      guardrailSystem: 'Guardrail system, parapet wall are greater than 39"',
      warningLine:
        'Warning line - hi vis tape 36" from surface >15\' from hazard',
      fallRestraintSystem:
        'Personal fall restraint system (harness, dropline, rope grab)',
      fallArrestSystem:
        'Personal fall arrest system (harness, lanyard, dropline, SRL)',
      fallProtectionOther: 'Other Fall Protection',
      otherFallProtectionSpecify: 'Specify Other Fall Protection',

      // Section 3: PROCEDURES FOR ASSEMBLY MAINT INSPECT DISASSEMBLY
      procedures:
        'Select all Procedures that Apply',
      walkToEquipment: 'Will walk directly to equipment/egress point',
      connectToEquipment: 'Connect directly to equipment/gear inspected',
      suitableAnchor:
        'Suitable anchor (4x restraint/5000lbs arrest/gear inspected)',
      droplineWithRopegrab:
        'Dropline w/ ropegrab lanyard full bdy harness/gear inspected',
      existingFallProtectAnchor:
        'Existing fall protect anchor fixed to roof/gear inspected',
      proceduresOther: 'Other Procedures',
      otherProceduresSpecify: 'Specify Other Procedures',

      // Section 4: METHODS OF OVERHEAD PROTECTION
      overheadProtection: 'Select all Methods of Overhead Protection that Apply',
      noFallingHazards: 'Do any falling object hazards exist?',
      hardHats: 'Hard hats',
      barricades: 'Barricades',
      warningSigns: 'Posted warning signs',
      materialSecured: 'Material secured when not in use',
      overheadProtectionOther: 'Other Overhead Protection',
      otherOverheadProtectionSpecify: 'Specify Other Overhead Protection',

      // Section 5: RESCUE PLAN
      rescuePlan: 'Select all Rescue Plan items that Apply',
      call911: 'Call 911',
      staffMonitoring: 'Building/customer staff is monitoring my safety',
      suspensionTrauma: 'Suspension/trauma attached to harness',
      utilizeAerialLift: 'Utilize aerial/scissor lift',
      utilizePreStageLadders: 'Utilize pre-stage ladders',
      rescuePlanOther: 'Other Rescue Plan',
      otherRescuePlanSpecify: 'Specify Other Rescue Plan',

      // Section 6: WORKING UNDER PLAN
      workingUnderPlan: 'Working Under Plan',
      techniciansWorkingUnderPlan: 'Technicians Working Under This Plan',
      additionalComments: 'Additional Comments',
      technicianName: 'Technician Name',
      technicianSubSection: 'Technician',



      // apprentice1Name: 'Apprentice 1 Name',
      // apprentice1SubSection: 'Apprentice 1',
      // apprentice1Signature: 'Apprentice 1 Signature',
      // apprentice2Name: 'Apprentice 2 Name',
      // apprentice2SubSection: 'Apprentice 2',
      // apprentice2Signature: 'Apprentice 2 Signature',
      // apprentice3Name: 'Apprentice 3 Name',
      // apprentice3SubSection: 'Apprentice 3',
      // apprentice3Signature: 'Apprentice 3 Signature',
    };
  }
}
